import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NavController } from '@ionic/angular';
import { first, Subject, takeUntil } from 'rxjs';
import { RtsDay } from 'src/app/dto/get-rts-result.dto';
import { HeaderDto } from 'src/app/dto/header.dto';
import { Order, SignEndOfDayRequest } from 'src/app/dto/order.dto';
import { OrderChangesService } from 'src/app/services/order/order-changes.service';
import { finalize, take } from 'rxjs/operators';
import { SyncAction } from '../../../../dto/sync-actions.enum';
import { OrdersService } from '../../../../services/order/orders.service';
import { DocTypes, FileService } from '../../../../services/file.service';
import { BackgroundSyncService } from '../../../../services/sync/background-sync.service';
import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/angular';
import { FilesHelperService } from '../../../../services/files-helper.service';

@Component({
  selector: 'app-end-day',
  templateUrl: './end-day.component.html',
  styleUrls: ['./end-day.component.scss']
})
/**
 * Экран завершения дня: подтверждение свайпом формирует расписку экспедитора (подпись конца дня).
 * Поток устойчив к оффлайну и не теряет юридически значимый документ:
 * - {@code endDay} помечает день завершённым и собирает payload подписи со стабильным ключом
 *   идемпотентности;
 * - пробуем подписать онлайн; при УСПЕХЕ проставляем id документа расписки, при сетевой ИЛИ логической
 *   ошибке (2xx с {@code success=false}) — ставим подпись в очередь синхронизации ({@link #queueSignDay})
 *   для повтора. Дубля не будет — сервер дедуплицирует по ключу.
 */
export class EndDayComponent implements OnInit, OnDestroy {
  targetUrl = window.location.pathname.split('/')[1];
  currentDayId: string = '';
  request: Order;
  dayData: RtsDay;
  loading: boolean = false;
  endDayConfirmed: boolean = false;
  expeditorDocGenerating: boolean = false;
  isDownloading: boolean = false;
  totalWeight: number = 0;
  headerDataValues: HeaderDto = {
    title: ''
  };

  expeditorNoteDocumentId?: number;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private navCtrl: NavController,
    private orderChangesService: OrderChangesService,
    private ordersService: OrdersService,
    private fileService: FileService,
    private backgroundSyncService: BackgroundSyncService,
    private filesHelper: FilesHelperService
  ) {}

  ngOnInit(): void {
    this.route.params.pipe(first()).subscribe(parameter => {
      this.currentDayId = parameter['id'];
    });

    this.orderChangesService
      .getOrderSubject(this.targetUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe(currentRequest => {
        if (currentRequest) {
          this.request = currentRequest;
          const dayData = this.request.getDayById(this.currentDayId);
          if (dayData) {
            this.dayData = dayData;
            this.dataUpdate();
          }
          this.dataUpdate();
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Пересчёт заголовка (номер дня) и суммарного веса нетто по машинам дня. */
  dataUpdate(): void {
    const dayNumber = this.request.getDayNumberById(this.dayData.orderNumber);
    this.headerDataValues.title = `День ${dayNumber}`;
    this.totalWeight = 0;
    this.dayData.vehicles.forEach(el => {
      if (el.loadingData && el.loadingData.netWeight) {
        this.totalWeight += el.loadingData.netWeight;
      }
    });
  }

  /**
   * Подтверждение завершения дня (свайп). Помечает день завершённым, собирает payload подписи и пробует
   * подписать онлайн; результат обрабатывается по правилам сохранности (см. обзор класса). Локальную
   * версию публикуем в {@code finalize} при любом исходе, чтобы завершённость дня сохранилась и офлайн.
   */
  confirmDay(event: boolean): void {
    if (!event) {
      return;
    }
    // Защита от повторного входа: два события свайпа в одном тике (до прогона change detection) иначе
    // дали бы двойную подпись — два updateRequests и две записи в очередь синхронизации (серверная
    // идемпотентность спасёт от дубля документа, но локальные эффекты отработали бы дважды).
    if (this.endDayConfirmed) {
      return;
    }

    this.endDayConfirmed = true;
    this.expeditorDocGenerating = true;

    try {
      const { order, endDayResult } = this.request.endDay(
        this.dayData.orderNumber
      );

      const updatedOrder = order;

      if (!endDayResult) {
        this.expeditorDocGenerating = false;
        return;
      }

      this.ordersService
        .signExpeditorNoteWithBES(endDayResult, order.bargainId)
        .pipe(
          take(1),
          takeUntil(this.destroy$),
          finalize(() => {
            this.orderChangesService.updateRequests(
              updatedOrder,
              this.targetUrl
            );
            this.expeditorDocGenerating = false;
          })
        )
        .subscribe({
          next: res => {
            // Логический неуспех при 2xx (success=false / нет id) — подпись не создана. Ставим в
            // очередь для повтора так же, как при сетевой ошибке: иначе юридически значимый документ
            // был бы молча утрачен (онлайн-путь принял бы неуспех за успех).
            if (!res.success || !res.data) {
              this.queueSignDay(endDayResult, updatedOrder);
              return;
            }
            try {
              const resultOrder = this.request.setForwarderNoteId(
                this.dayData.orderNumber,
                res.data
              );
              Object.assign(updatedOrder, resultOrder);
              this.expeditorNoteDocumentId = res.data;
            } catch (error) {
              Sentry.captureException(error);
            }
          },
          error: error => {
            Sentry.captureException(error);
            this.queueSignDay(endDayResult, updatedOrder);
          }
        });
    } catch (error) {
      Sentry.captureException(error);
      this.expeditorDocGenerating = false;
    }
  }

  async downloadExpeditorNote() {
    if (this.isDownloading) {
      return;
    }

    try {
      this.isDownloading = true;
      const fileName = `Расписка экспедитора-${this.request.contractName}.pdf`;
      const filePath = await this.fileService.downloadFileForDeal(
        Number(this.expeditorNoteDocumentId),
        fileName,
        DocTypes.FORWARDER_RECEIPT
      );

      // Открываем файл только на нативной платформе (по непустому пути).
      if (filePath && Capacitor.isNativePlatform()) {
        await this.filesHelper.openFile(filePath, 'application/pdf');
      }
      // В web файл уже скачан внутри downloadFileForDeal.
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      this.isDownloading = false;
    }
  }

  navBack() {
    this.navCtrl.pop();
  }

  /**
   * Ставит подпись конца дня в очередь офлайн-синхронизации для повтора. Ключ идемпотентности внутри
   * payload гарантирует, что сервер не создаст дубль, даже если исходная попытка всё же дошла.
   */
  private queueSignDay(payload: SignEndOfDayRequest, order: Order): void {
    this.backgroundSyncService.addSyncAction(
      {
        payload,
        order,
        dayId: this.dayData.orderNumber,
        bargainId: order.bargainId
      },
      SyncAction.signDay
    );
  }
}
